import { from } from 'rxjs';
import { distinct, filter, map, toArray } from 'rxjs/operators';
import moment from 'moment';
import { ErrorObject } from 'ajv';
// todo typings

const requiredMinItems = function(e: any, r: string) {
  return r === e.dataPath ? (e.message || 'Required') : null;
};

const requiredType = function(e: any, r: string) {
  if (r === e.dataPath) {
    if (Array.isArray(e.params.type)) {
      return 'Should be ' + e.params.type.filter((t: string) => t !== 'null').join(', ');  
    } else {
      return e.message;
    }
  }

  return null;
};

const required = function(e: any, r: string) {
  return r === e.dataPath + '/' + e.params.missingProperty ? 'Required' : null;
};

const maxProperties = function(e: any, r: string) {
  return r === e.dataPath ? 'No more than ' + e.params.limit + ' value(s) allowed' : null;
};

const minProperties = function(e: any, r: string) {
  return r === e.dataPath ? 'Requires at least ' + e.params.limit + ' value(s)' : null;
};

const additionalProperties = function(e: any, r: string) {
  return r === e.dataPath ? 'Undeclared value not allowed' : null;
};

const dateFormats = {
  date: 'MM/DD/YYYY',
  dateTime: 'hh:mm A on MM/DD/YYYY',
  time: 'hh:mm A'
};

function isDateTime(e: string) {
  return /^\d{4}-\d{2}-\d{2}T/.test(e);
}

function isDate(e: string) {
  return /^\d{4}-\d{2}-\d{2}$/.test(e);
}

function formatError(e: any, r: string) {
  if (!e.params?.limit) {
    return 'Invalid date';
  }
  return 'Should be ' + r + (e.params.exclusive ? '' : '=') + ' ' + translateDateTimeLimit(e.params.limit)
}

function translateDateTimeLimit(e: string | Date | number) {
  if (isDateTime(e as string)) {
    return '' + moment(e as string).format(dateFormats.dateTime);
  } else if (isDate(e as string)) {
    return '' + moment(e as string).format(dateFormats.date);
  }
  const r = (e as string).substring(0, 5);
  const t = Number.parseInt(r.substring(0, 2));
  return r + ' ' + (t >= 12 ? 'PM' : 'AM')
}

const formatTranslations: Record<string, string> = {
  email: 'email address',
  uri: 'URI',
  'date-time': 'date',
  date: 'date',
  time: 'time'
};
const format = function(e: any, r: string) {
  return r === e.dataPath ? 'Should be valid ' + formatTranslations[e.params.format] : null
};

function lengthError(e: any, r: string) {
  const t = e.params.limit;
  return 'Should be at ' + r + ' ' + t + ' character' + (t === 1 ? '' : 's')
}
function enumFunc(e: any) {
  const r = e.params.allowedValues;
  return typeof r[0] === 'number' ? 'Should be ' + r.slice(0, r.length - 1).join(', ') + ', or ' + r[r.length - 1] : 'Value not allowed'
};

/**
 * For null values that should be not-null, show "Required" to indicate that the value is missing.
 * This is more clear than "x should be a string" or "x should be a number"
 */
 function capitalizeMessage(e: any) {
  return '' + e.message.charAt(0).toUpperCase() + e.message.slice(1)
}
const translations: Record<string, any> = {
  required: required,
  maxProperties: maxProperties,
  minProperties: minProperties,
  additionalProperties: additionalProperties,
  type: requiredType,
  multipleOf: capitalizeMessage,
  maximum: capitalizeMessage,
  exclusiveMaximum: capitalizeMessage,
  minimum: capitalizeMessage,
  exclusiveMinimum: capitalizeMessage,
  minItems: requiredMinItems,
  formatMaximum: function(e: string) {
    return formatError(e, '<');
  },
  formatMinimum: function(e: string) {
    return formatError(e, '>');
  },
  format: format,
  maxLength: function(e: any) {
    return lengthError(e, 'most');
  },
  minLength: function(e: any) {
    return lengthError(e, 'least');
  },
  pattern: function() {
    return 'Doesn\'t match expected pattern';
  },
  const: function(e: any) {
    if (typeof e.params.allowedValue === 'number') {
      return `Must = ${e.params.allowedValue}`;
    }
    return `Should be '${e.params.allowedValue}'`;
  },
  enum: enumFunc,
  not: function(e: any) {
    return 'Value not allowed';
  }
};


/**
 * Generate a human-friendly message for a data path's errors
 * @param errors the Ajv Error Objects output from the validator function
 * @param pathWatched the path that the translation is serving translation for. Only errors directly applicable to the path and not any subpaths will be translated.
 * @return a string consisting of all error information, or `null`
 */
export function translateErrors(errors: ErrorObject[], pathWatched: string): null | string | undefined {
    // fix typings, async setter. why async?
    if (errors.length === 0) {
      return null;
    }

    let t: string | undefined = undefined;
    
    from(errors.filter(e => {
      return e.keyword !== 'anyOf' && !(e.keyword === 'type' && (e.params as any).type === 'null');
    }))
      .pipe(
        map(function(e: any) {
          const t = (translations as any)[e.keyword];
          return t ? t(e, pathWatched) : e.message;
        }),
        filter(function(e) {
          return e !== null;
        }),
        distinct(),
        toArray()
      )
      .subscribe(function(e) {
        const weighted = e.sort(m => {
          if (m === 'Required') {
            return -1;
          } else {
            return 1;
          }
        })
        return t = weighted[0];
      })
      .unsubscribe();
    
    return t;
}
